import React, { useCallback, useState, createContext, useContext } from 'react';
type Lang = 'en' | 'es';
interface LanguageContextType {
  lang: Lang;
  toggleLang: () => void;
  t: (key: string) => string;
}
const translations: Record<string, Record<Lang, string>> = {
  // Navbar
  'nav.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'nav.matches': {
    en: 'Matches',
    es: 'Partidos'
  },
  'nav.deals': {
    en: 'Deals',
    es: 'Ofertas'
  },
  'nav.liveScores': {
    en: 'Live Scores',
    es: 'Resultados en Vivo'
  },
  'nav.orderYours': {
    en: 'Order Yours',
    es: 'Compra la Tuya'
  },
  // Hero
  'hero.badge': {
    en: "You're now part of Uruguay 2026",
    es: 'Ahora eres parte de Uruguay 2026'
  },
  'hero.title1': {
    en: "Don't Just Support Uruguay,",
    es: 'No Solo Apoyes a Uruguay,'
  },
  'hero.title2': {
    en: 'Live It',
    es: 'Vívelo'
  },
  'hero.subtitle': {
    en: 'Updated in real live time. Access exclusive content, match updates, and travel deals directly from your digital hub.',
    es: 'Actualizado en tiempo real. Accede a contenido exclusivo, actualizaciones de partidos y ofertas de viaje directamente desde tu hub digital.'
  },
  'hero.cta1': {
    en: 'Watch Matches',
    es: 'Ver Partidos'
  },
  'hero.cta2': {
    en: 'Explore Team',
    es: 'Explorar Equipo'
  },
  'hero.scroll': {
    en: 'Scroll to discover',
    es: 'Desliza para descubrir'
  },
  // Team Overview
  'team.label': {
    en: 'Our Legacy',
    es: 'Nuestro Legado'
  },
  'team.title1': {
    en: 'A Nation Forged',
    es: 'Una Nación Forjada'
  },
  'team.title2': {
    en: 'In Glory',
    es: 'En la Gloria'
  },
  'team.desc': {
    en: "Uruguay is the smallest country to ever win a World Cup. La Celeste's legacy is built on garra charrúa - the fighting spirit that has made them giants of world football since 1900. From the first World Cup in 1930 to modern-day excellence, Uruguay punches far above its weight.",
    es: 'Uruguay es el país más pequeño en ganar una Copa del Mundo. El legado de La Celeste se basa en la garra charrúa, el espíritu de lucha que los ha convertido en gigantes del fútbol mundial desde 1900. Desde el primer Mundial en 1930 hasta la excelencia actual, Uruguay siempre supera las expectativas.'
  },
  'team.since': {
    en: 'Since 1900',
    es: 'Desde 1900'
  },
  'team.history': {
    en: 'Explore Our Full History →',
    es: 'Explora Nuestra Historia Completa →'
  },
  'team.euro': {
    en: 'World Cup',
    es: 'Copa del Mundo'
  },
  'team.euroDesc': {
    en: '2x Champions (1930, 1950)',
    es: '2x Campeones (1930, 1950)'
  },
  'team.nationsLeague': {
    en: 'Copa América',
    es: 'Copa América'
  },
  'team.nationsLeagueDesc': {
    en: '15x Champions',
    es: '15x Campeones'
  },
  'team.euros': {
    en: 'Olympic Gold',
    es: 'Oro Olímpico'
  },
  'team.eurosDesc': {
    en: '2x Winners (1924, 1928)',
    es: '2x Ganadores (1924, 1928)'
  },
  'team.worldCups': {
    en: '14 World Cups',
    es: '14 Mundiales'
  },
  'team.worldCupsDesc': {
    en: 'Global Stage Appearances',
    es: 'Participaciones Mundiales'
  },
  'team.videoLabel': {
    en: 'Garra Charrúa',
    es: 'Garra Charrúa'
  },
  'team.videoTitle1': {
    en: 'One Nation, One',
    es: 'Una Nación, Un'
  },
  'team.videoTitle2': {
    en: 'Heartbeat',
    es: 'Latido'
  },
  // Key Players
  'players.label': {
    en: 'The Squad',
    es: 'El Plantel'
  },
  'players.title1': {
    en: 'The Stars of',
    es: 'Las Estrellas de'
  },
  'players.title2': {
    en: 'Uruguay',
    es: 'Uruguay'
  },
  'players.viewSquad': {
    en: 'View Full Squad on ESPN.com',
    es: 'Ver Plantel Completo en ESPN.com'
  },
  'players.forward': {
    en: 'Forward',
    es: 'Delantero'
  },
  'players.midfielder': {
    en: 'Midfielder',
    es: 'Mediocampista'
  },
  'players.defender': {
    en: 'Defender',
    es: 'Defensa'
  },
  // Match Center
  'matches.label': {
    en: 'World Cup 2026',
    es: 'Mundial 2026'
  },
  'matches.title1': {
    en: 'Match',
    es: 'Centro de'
  },
  'matches.title2': {
    en: 'Center',
    es: 'Partidos'
  },
  'matches.desc': {
    en: 'Follow La Celeste in Group C. Get live updates, official lineups, and real-time scores from the biggest stage in football.',
    es: 'Sigue a La Celeste en el Grupo C. Obtén actualizaciones en vivo, alineaciones oficiales y resultados en tiempo real del mayor escenario del fútbol.'
  },
  'matches.standings': {
    en: 'Group Standings',
    es: 'Clasificación del Grupo'
  },
  'matches.live': {
    en: 'Live',
    es: 'En Vivo'
  },
  'matches.autoRefresh': {
    en: 'Auto-refreshes every 5 min',
    es: 'Se actualiza cada 5 min'
  },
  'matches.refreshNow': {
    en: 'Refresh Now',
    es: 'Actualizar Ahora'
  },
  'matches.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'matches.viewStandings': {
    en: 'View Live Standings on FlashScore',
    es: 'Ver Clasificación en FlashScore'
  },
  'matches.nextMatch': {
    en: 'Next Match',
    es: 'Próximo Partido'
  },
  'matches.espn': {
    en: 'ESPN Schedule',
    es: 'Calendario ESPN'
  },
  'matches.sofascore': {
    en: 'SofaScore',
    es: 'SofaScore'
  },
  'matches.flashscore': {
    en: 'FlashScore Live',
    es: 'FlashScore en Vivo'
  },
  'matches.foxsports': {
    en: 'Fox Sports',
    es: 'Fox Sports'
  },
  // Stats
  'stats.label': {
    en: 'Uruguay By The Numbers',
    es: 'Uruguay en Números'
  },
  'stats.worldCup': {
    en: 'World Cup Appearances',
    es: 'Participaciones en Mundiales'
  },
  'stats.titles': {
    en: 'Copa América Titles',
    es: 'Títulos de Copa América'
  },
  'stats.goals': {
    en: 'World Cup Titles',
    es: 'Títulos de Copa del Mundo'
  },
  'stats.ronaldo': {
    en: 'Suárez Goals (Record)',
    es: 'Goles de Suárez (Récord)'
  },
  // Historic Moments
  'history.label': {
    en: 'The Archives',
    es: 'Los Archivos'
  },
  'history.title1': {
    en: 'Moments That',
    es: 'Momentos Que'
  },
  'history.title2': {
    en: 'Made History',
    es: 'Hicieron Historia'
  },
  'history.euro2016': {
    en: '1930 World Cup Victory',
    es: 'Victoria Mundial 1930'
  },
  'history.eder': {
    en: '1950 Maracanazo',
    es: 'El Maracanazo 1950'
  },
  'history.ronaldoGoals': {
    en: 'Suárez Handball vs Ghana',
    es: 'Mano de Suárez vs Ghana'
  },
  'history.nationsLeague': {
    en: 'Forlán Golden Ball 2010',
    es: 'Balón de Oro Forlán 2010'
  },
  'history.copa2011': {
    en: 'Copa América 2011',
    es: 'Copa América 2011'
  },
  'history.enzo': {
    en: 'Enzo Francescoli',
    es: 'Enzo Francescoli'
  },
  'history.recoba': {
    en: 'Álvaro Recoba Best Goals',
    es: 'Mejores Goles de Recoba'
  },
  'history.copa2024': {
    en: 'Copa América 2024',
    es: 'Copa América 2024'
  },
  // Fan Experience
  'fan.label': {
    en: 'More Than A Game',
    es: 'Más Que Un Juego'
  },
  'fan.title1': {
    en: 'Feel the Passion of',
    es: 'Siente la Pasión de'
  },
  'fan.title2': {
    en: 'Uruguay',
    es: 'Uruguay'
  },
  'fan.desc': {
    en: 'Join millions of voices singing as one. Wear the colors, feel the history, and be part of the journey to global glory.',
    es: 'Únete a millones de voces cantando como una sola. Viste los colores, siente la historia y sé parte del viaje hacia la gloria mundial.'
  },
  // Tickets / Travel Deals
  'deals.label': {
    en: 'Travel to the World Cup',
    es: 'Viaja al Mundial'
  },
  'deals.title1': {
    en: 'Exclusive',
    es: 'Ofertas Exclusivas'
  },
  'deals.title2': {
    en: 'Fan Deals',
    es: 'para Fans'
  },
  'deals.desc': {
    en: 'Exclusive discounts for Uruguay fans on tickets, hotels, flights & transport to support La Celeste in North America.',
    es: 'Descuentos exclusivos para fans de Uruguay en entradas, hoteles, vuelos y transporte para apoyar a La Celeste en Norteamérica.'
  },
  'deals.unlockVip': {
    en: 'Unlock VIP Discounts',
    es: 'Desbloquea Descuentos VIP'
  },
  'deals.applyCode': {
    en: 'Apply code at checkout on partner sites',
    es: 'Aplica el código en el checkout'
  },
  'deals.tickets': {
    en: 'Match Tickets',
    es: 'Entradas para Partidos'
  },
  'deals.ticketsDesc': {
    en: 'Up to 15% off group stage packages',
    es: 'Hasta 15% de descuento en fase de grupos'
  },
  'deals.findTickets': {
    en: 'Find Tickets',
    es: 'Buscar Entradas'
  },
  'deals.hotels': {
    en: 'Hotels & Accommodation',
    es: 'Hoteles y Alojamiento'
  },
  'deals.hotelsDesc': {
    en: 'Partner hotel deals near match venues',
    es: 'Ofertas de hoteles cerca de los estadios'
  },
  'deals.bookHotels': {
    en: 'Book Hotels',
    es: 'Reservar Hoteles'
  },
  'deals.flights': {
    en: 'Flights',
    es: 'Vuelos'
  },
  'deals.flightsDesc': {
    en: 'Special fares from Montevideo to USA',
    es: 'Tarifas especiales desde Montevideo a EE.UU.'
  },
  'deals.searchFlights': {
    en: 'Search Flights',
    es: 'Buscar Vuelos'
  },
  'deals.transport': {
    en: 'Local Transport',
    es: 'Transporte Local'
  },
  'deals.transportDesc': {
    en: 'Shuttle services & car rentals at match cities',
    es: 'Servicios de traslado y alquiler de autos'
  },
  'deals.planTransport': {
    en: 'Plan Transport',
    es: 'Planear Transporte'
  },
  // Fan HQ / Gallery
  'fanHq.label': {
    en: 'Everything You Need for Uruguay 2026',
    es: 'Todo lo que Necesitas para Uruguay 2026'
  },
  'fanHq.title1': {
    en: 'Fan',
    es: 'Sede del'
  },
  'fanHq.title2': {
    en: 'HQ',
    es: 'Hincha'
  },
  'fanHq.countdown': {
    en: "Countdown to Uruguay's First Match",
    es: 'Cuenta Regresiva para el Primer Partido'
  },
  'fanHq.days': {
    en: 'Days',
    es: 'Días'
  },
  'fanHq.hrs': {
    en: 'Hrs',
    es: 'Hrs'
  },
  'fanHq.min': {
    en: 'Min',
    es: 'Min'
  },
  'fanHq.sec': {
    en: 'Sec',
    es: 'Seg'
  },
  'fanHq.capacity': {
    en: 'Capacity',
    es: 'Capacidad'
  },
  'fanHq.chants': {
    en: 'Learn the',
    es: 'Aprende los'
  },
  'fanHq.chantsHighlight': {
    en: 'Chants',
    es: 'Cánticos'
  },
  'fanHq.nationalAnthem': {
    en: 'National Anthem',
    es: 'Himno Nacional'
  },
  'fanHq.classicChant': {
    en: 'Classic Stadium Chant',
    es: 'Cántico Clásico'
  },
  'fanHq.crowdFavorite': {
    en: 'Crowd Favorite',
    es: 'Favorito de la Hinchada'
  },
  'fanHq.victoryChant': {
    en: 'Victory Chant',
    es: 'Cántico de Victoria'
  },
  'fanHq.watchYoutube': {
    en: 'Watch on YouTube',
    es: 'Ver en YouTube'
  },
  'fanHq.phrases': {
    en: 'Essential',
    es: 'Frases'
  },
  'fanHq.phrasesHighlight': {
    en: 'Phrases',
    es: 'Esenciales'
  },
  'fanHq.quickLinks': {
    en: 'Quick',
    es: 'Enlaces'
  },
  'fanHq.quickLinksHighlight': {
    en: 'Links',
    es: 'Rápidos'
  },
  'fanHq.whereToWatch': {
    en: 'Where to Watch',
    es: 'Dónde Ver'
  },
  'fanHq.whereToWatchDesc': {
    en: 'Streaming & TV channels',
    es: 'Canales de TV y Streaming'
  },
  'fanHq.fanZones': {
    en: 'Fan Info Channel',
    es: 'Canal de Info para Fans'
  },
  'fanHq.fanZonesDesc': {
    en: 'Official FIFA fan information',
    es: 'Información oficial de FIFA'
  },
  'fanHq.fifaRanking': {
    en: 'FIFA Ranking',
    es: 'Ranking FIFA'
  },
  'fanHq.fifaRankingDesc': {
    en: 'Official world rankings',
    es: 'Ranking mundial oficial'
  },
  'fanHq.fullSchedule': {
    en: 'Full WC Schedule',
    es: 'Calendario del Mundial'
  },
  'fanHq.fullScheduleDesc': {
    en: 'All 104 matches',
    es: 'Los 104 partidos'
  },
  // Footer
  'footer.desc': {
    en: 'An official Qtap by STDI digital product. Your smart NFC T-shirt hub for Uruguay 2026 — real-time match data, exclusive content, and travel deals at your fingertips.',
    es: 'Un producto digital oficial de Qtap by STDI. Tu hub de camiseta NFC inteligente para Uruguay 2026 — datos de partidos en tiempo real, contenido exclusivo y ofertas de viaje al alcance de tu mano.'
  },
  'footer.resources': {
    en: 'Resources',
    es: 'Recursos'
  },
  'footer.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'footer.firstTeam': {
    en: 'First Team',
    es: 'Primer Equipo'
  },
  'footer.matches': {
    en: 'Matches',
    es: 'Partidos'
  },
  'footer.standings': {
    en: 'Standings',
    es: 'Clasificación'
  },
  'footer.travelDeals': {
    en: 'Travel Deals',
    es: 'Ofertas de Viaje'
  },
  'footer.support': {
    en: 'Support',
    es: 'Soporte'
  },
  'footer.contactUs': {
    en: 'Contact Us',
    es: 'Contáctanos'
  },
  'footer.faq': {
    en: 'FAQ',
    es: 'Preguntas Frecuentes'
  },
  'footer.privacy': {
    en: 'Privacy Policy',
    es: 'Política de Privacidad'
  },
  'footer.terms': {
    en: 'Terms of Service',
    es: 'Términos de Servicio'
  },
  'footer.cookies': {
    en: 'Cookie Policy',
    es: 'Política de Cookies'
  },
  'footer.copyright': {
    en: 'Qtap by STDI. All rights reserved.',
    es: 'Qtap by STDI. Todos los derechos reservados.'
  }
};
const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  toggleLang: () => {},
  t: (key: string) => key
});
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLang] = useState<Lang>('en');
  const toggleLang = useCallback(() => {
    setLang(prev => prev === 'en' ? 'es' : 'en');
  }, []);
  const t = useCallback((key: string) => {
    return translations[key]?.[lang] || key;
  }, [lang]);
  return <LanguageContext.Provider value={{
    lang,
    toggleLang,
    t
  }} data-id="element-211">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  return useContext(LanguageContext);
}